import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { LocalizedString, PartnerType, ProductType, CreatePriceRequest } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { AddPriceComponent, AddPriceResult } from '@pages/pricing/components/add-price/add-price.component';
import { LocalizationService } from '@services/localization.service';
import { PartnerItem } from '@services/partners.service';
import { ProductsService } from '@services/products.service';
import { IntegrationConfig } from '@services/integration-manager.service';
import { BasePartnerCreationComponent } from '../base-item-creation.component';
import { IntegrationComponent } from '../components/integration/integration.component';
import { PriceListComponent } from '../components/price-list/price-list.component';
import { CreatePageHeaderComponent } from '../create-page-header/create-page-header.component';
import { SLAUGHTERHOUSE_INTEGRATION_CONFIGS } from '../partner-integration.configs';
import { environment } from '@environments/environment';

@Component({
  selector: 'md-create-slaughterhouse',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    CreatePageHeaderComponent,
    TranslateModule,
    IntegrationComponent,
    AddPriceComponent,
    PriceListComponent
  ],
  templateUrl: './create-slaughterhouse.component.html',
  styleUrl: './create-slaughterhouse.component.scss',
  schemas: []
})
export class CreateSlaughterhouseComponent extends BasePartnerCreationComponent<PartnerItem> implements OnInit, OnDestroy {
  private readonly TOTAL_STEPS = 3;
  private readonly localizationService = inject(LocalizationService);
  private readonly productsService = inject(ProductsService);
  hubSpotFormId = '76f8d088-aa02-4294-ae51-4f839cb1bf39';
  hubSpotPortalId = environment.hubspot.portalId;

  protected override getPartnerIntegrationConfigs(): IntegrationConfig[] {
    return SLAUGHTERHOUSE_INTEGRATION_CONFIGS;
  }

  protected override getTotalSteps(): number {
    return this.TOTAL_STEPS;
  }

  protected override getDetailsRoute(partnerId: string): string[] {
    return ['/app/view/partners/slaughterhouses', partnerId];
  }

  protected override async fetchDataForPricing(): Promise<void> {
    this.isLoading.set(true);
    if (!this.primaryItemData?.id || !this.primaryItemData?.['name']) {
      this.errorService.showError(this.translateService.instant('CREATE_SLAUGHTERHOUSE.ERRORS.HUBSPOT_DATA_MISSING'));
      this.isLoading.set(false);
      return;
    }
    try {
      const productsResponse = await this.productsService.getByProducts();
      const serviceProductsResponse = await this.productsService.getServices();
      const allProducts = [...(productsResponse.data || []), ...(serviceProductsResponse.data || [])];

      let availableProducts = allProducts;
      try {
        const existingPrices = await this.pricingService.getPricingForSlaughterhouse(this.primaryItemData.id);
        const existingProductIds = new Set(existingPrices.data?.map(p => p.productId) || []);
        availableProducts = allProducts.filter(product => !existingProductIds.has(product.id));
      } catch (error) {
        console.warn('Could not fetch existing prices for filtering:', error);
      }

      this.priceDataForChild.set({
        items: availableProducts,
        measure: this.pricingService.measures
      });
      this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
    } catch (error) {
      console.error('Error fetching data for pricing:', error);
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.PRICING_DATA_PREPARE'));
    } finally {
      this.isLoading.set(false);
    }
  }

  protected override createPriceObject(input: AddPriceResult): CreatePriceRequest | undefined {
    if (!this.primaryItemData?.id) {
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.PRIMARY_PARTNER_DATA_MISSING'));
      return undefined;
    }
    return {
      name: input.itemName,
      productType: 'slaughterhouse' as ProductType,
      productId: input.itemId,
      partnerType: 'slaughterhouse' as PartnerType,
      partnerId: this.primaryItemData.id,
      rates: [{ rate: input.price, validFrom: input.validFrom.toISOString() }],
      unit: input.unit,
      productName: input.itemName,
      partnerName: this.getItemNameLocalized(this.primaryItemData?.['name'] as string | LocalizedString | undefined)
    };
  }

  localizedPartnerNameForDisplay(): string {
    if (!this.primaryItemData?.['name']) {
      return '';
    }

    const name = this.primaryItemData['name'];
    if (typeof name === 'string') {
      return name;
    } else {
      return (name as LocalizedString)[this.localizationService.currentLang] || '';
    }
  }

  public override onPriceAdded(price: AddPriceResult): void {
    this.priceListSignal.update(prices => [...prices, price]);
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
  }

  onDeletePrice(price: AddPriceResult): void {
    this.priceListSignal.update(prices => prices.filter(p => p.itemId !== price.itemId || p.validFrom.getTime() !== price.validFrom.getTime()));
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICE_REMOVED'));
  }

  async onDone(): Promise<void> {
    try {
      this.isLoading.set(true);
      await this.onSaveDone();
      this.notificationService.success(this.translateService.instant('NOTIFICATIONS.SUCCESS.CREATION_COMPLETE'));
      this.router.navigate(['/app/partners/slaughterhouses']);
    } catch (error) {
      console.error('Error completing slaughterhouse creation:', error);
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.COMPLETION_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
